import {
  CCI_ACCOUNT_TYPE_CODES,
  DEPRECATED_BANK_ACCOUNT_REPLACEMENTS,
} from '@/shared/common/constants/bankAccountType.constant';
import type { CciAccountTypeCode } from '@/shared/common/types/bankAccountType.type';
import type { BankAccounts, BankAccountType } from '@/types/WalletType';

type DeprecatedBankAccountType = keyof typeof DEPRECATED_BANK_ACCOUNT_REPLACEMENTS;

/**
 * Indica si el código corresponde a una cuenta CCI de Perú (`LP_CCI` o `LP_CCI_ALT`).
 * @param code - Código de tipo de cuenta bancaria a evaluar.
 * @returns `true` cuando el código pertenece al grupo CCI.
 */
export function isCciAccountType(code?: string | null): code is CciAccountTypeCode {
  if (!code) return false;
  return CCI_ACCOUNT_TYPE_CODES.includes(code as CciAccountTypeCode);
}

/**
 * Indica si el código está marcado como deprecado en {@link DEPRECATED_BANK_ACCOUNT_REPLACEMENTS}.
 * @param code - Código de tipo de cuenta bancaria a evaluar.
 */
export function isDeprecatedBankAccountType(code?: string | null): code is DeprecatedBankAccountType {
  if (!code) return false;
  return code in DEPRECATED_BANK_ACCOUNT_REPLACEMENTS;
}

/**
 * Retorna el tipo sucesor de un código deprecado, si está definido en el mapa.
 */
function getDeprecatedBankAccountReplacement(
  code: DeprecatedBankAccountType
): string {
  return DEPRECATED_BANK_ACCOUNT_REPLACEMENTS[code];
}

/**
 * Resuelve la cuenta default visible aplicando reemplazos del mapa de deprecación.
 * Si el default es legacy y su sucesor está en el arreglo, retorna el sucesor.
 * Si el default es legacy sin sucesor presente, retorna `undefined`.
 * @param bankAccounts - Cuentas bancarias de la wallet.
 */
export function resolveDefaultBankAccount(
  bankAccounts: BankAccounts[] | undefined
): BankAccounts | undefined {
  if (!bankAccounts?.length) return undefined;

  const codes = new Set(bankAccounts.map((account) => account.bankAccountTypeCode));
  const defaultAccount = bankAccounts.find((account) => account.defaultAccount);
  const defaultCode = defaultAccount?.bankAccountTypeCode;

  if (!defaultAccount || !defaultCode) return undefined;

  if (isDeprecatedBankAccountType(defaultCode)) {
    const replacement = getDeprecatedBankAccountReplacement(defaultCode);

    if (codes.has(replacement as BankAccountType)) {
      return bankAccounts.find((account) => account.bankAccountTypeCode === replacement);
    }

    return undefined;
  }

  return defaultAccount;
}

/**
 * Oculta tipos deprecados de un arreglo (métodos de pago, cuentas, etc.).
 * @param items - Elementos a filtrar.
 * @param getCode - Función que extrae el código de tipo de cada elemento.
 */
export function filterDeprecatedBankAccountTypes<T>(
  items: T[],
  getCode: (item: T) => string | undefined | null
): T[] {
  return items.filter((item) => !isDeprecatedBankAccountType(getCode(item)));
}

/**
 * Normaliza tipos CCI alternos al código base usado para labels, copy y lookups compartidos.
 * @param code - Código de tipo de cuenta bancaria.
 * @returns `LP_CCI` para variantes CCI; el mismo código para el resto de tipos.
 */
export function resolveCciAccountLookupType(code: BankAccountType): BankAccountType {
  return isCciAccountType(code) ? 'LP_CCI' : code;
}

/**
 * Determina si un tipo de cuenta bancaria corresponde al método de pago seleccionado.
 * Trata `LP_CCI` y `LP_CCI_ALT` como equivalentes; el resto de tipos requiere coincidencia exacta.
 * @param bankAccountTypeCode - Código del bank account (típicamente el default de la wallet).
 * @param paymentMethodName - Nombre del método de pago elegido en cash-in.
 * @returns `true` cuando ambos identificadores representan la misma cuenta local de pago.
 */
export function matchesPaymentBankAccountType(
  bankAccountTypeCode?: string | null,
  paymentMethodName?: string | null
): boolean {
  if (!bankAccountTypeCode || !paymentMethodName) return false;
  if (bankAccountTypeCode === paymentMethodName) return true;

  return (
    isCciAccountType(bankAccountTypeCode) &&
    resolveCciAccountLookupType(bankAccountTypeCode as BankAccountType) ===
      resolveCciAccountLookupType(paymentMethodName as BankAccountType)
  );
}
